/* The command palette — "/" (or Cmd/Ctrl+K) from anywhere: jump to any
   surface, run a shell action (new build, talk to AI, customize widgets,
   theme), or ask AI. Fuzzy-ish prefix/substring match, full keyboard: ↑↓
   navigate, Enter runs, Esc closes. Focus is trapped in the input; the
   listbox pattern keeps it AA (aria-activedescendant, roles, labels). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAV, NavIcon } from '../nav';
import { Kicker } from '../ui';

export interface PaletteAction {
  id: string;
  label: string;
  hint: string; // mono microcopy: group / consequence
  icon?: React.ReactNode;
  run: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  actions: PaletteAction[]; // shell-provided actions (new build, talk, theme…)
}

export function CommandPalette({ open, onClose, actions }: Props) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const all = useMemo<PaletteAction[]>(
    () => [
      ...NAV.map((n) => ({
        id: `go-${n.id}`,
        label: n.label,
        hint: `surface · ${n.path}`,
        icon: <NavIcon id={n.id} size={16} />,
        run: () => nav(n.path),
      })),
      ...actions,
    ],
    [nav, actions],
  );

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((a) => a.label.toLowerCase().includes(needle) || a.hint.toLowerCase().includes(needle));
  }, [q, all]);

  useEffect(() => setSel(0), [q, open]);

  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement as HTMLElement | null;
      setQ('');
      // Focus after the dialog paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      openerRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  const runSel = () => {
    const a = results[sel];
    if (!a) return;
    onClose();
    a.run();
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 'var(--z-overlay)' as unknown as number, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 'max(12vh, env(safe-area-inset-top, 0px) + 40px)' }}
    >
      <div
        role="dialog"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, calc(100vw - 28px))', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 15px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ display: 'flex', color: 'var(--muted)', flex: 'none' }} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx={11} cy={11} r={7} />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); onClose(); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); runSel(); }
            }}
            placeholder="Jump to a surface, run an action, ask AI…"
            aria-label="Command palette"
            role="combobox"
            aria-expanded="true"
            aria-controls="mc-palette-list"
            aria-activedescendant={results[sel] ? `mc-pal-${results[sel].id}` : undefined}
            style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: 'var(--ink)', fontFamily: 'var(--font-body)', fontSize: 14.5 }}
          />
          <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 5, padding: '1px 5px', flex: 'none' }}>esc</kbd>
        </div>
        <div id="mc-palette-list" role="listbox" aria-label="Results" className="mc-scroll" style={{ maxHeight: 'min(50dvh, 420px)', overflow: 'auto', padding: 6 }}>
          {results.length === 0 && (
            <div style={{ padding: '18px 14px', color: 'var(--muted)', fontSize: 13.5 }}>
              Nothing matches “{q}”. <Kicker>// try a surface name or an action</Kicker>
            </div>
          )}
          {results.map((a, i) => (
            <div
              key={a.id}
              id={`mc-pal-${a.id}`}
              role="option"
              aria-selected={i === sel}
              onMouseEnter={() => setSel(i)}
              onClick={() => { onClose(); a.run(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: '11px 12px', borderRadius: 'var(--radius-md)',
                background: i === sel ? 'var(--primary-soft)' : 'transparent',
                border: `1px solid ${i === sel ? 'var(--primary-line)' : 'transparent'}`,
                cursor: 'pointer', minHeight: 44,
              }}
            >
              <span style={{ display: 'flex', color: i === sel ? 'var(--primary)' : 'var(--muted)', flex: 'none' }} aria-hidden="true">
                {a.icon ?? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: i === sel ? 600 : 400, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
              <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{a.hint}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
